'use client'
import React from 'react'
import { motion } from 'motion/react'
import { useInView } from 'react-intersection-observer'
import { getMediaUrl } from '@/utilities/getMediaUrl'
import Image from 'next/image'
import { imgproxyEnabled, imgproxyLoader } from '@/utilities/imgproxy'
import type { Media } from 'src/payload-types'

type SimpleImageGridBlock = {
  images: {
    image: string | Media
    id?: string | null
  }[]
  id?: string | null
  blockName?: string | null
  blockType: 'simpleImageGrid'
}

type Props = {
  className?: string
} & SimpleImageGridBlock

export const SimpleImageGrid: React.FC<Props> = ({ images }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  }

  return (
    <section className="bg-white py-24">
      <div className="container mx-auto px-6">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
        >
          {images.map((item: SimpleImageGridBlock['images'][0], index: number) => (
            <SimpleImageGridItem
              image={item.image}
              index={index}
              itemVariants={itemVariants}
              key={index}
            />
          ))}
        </motion.div>
      </div>
    </section>
  )
}

const SimpleImageGridItem = ({
  image,
  index,
  itemVariants,
}: {
  image: string | Media
  index: number
  itemVariants: any
}) => {
  return (
    <motion.div
      variants={itemVariants}
      className="group relative aspect-square overflow-hidden rounded-lg"
    >
      <Image
        loader={imgproxyEnabled ? imgproxyLoader : undefined}
        src={getMediaUrl(image)}
        alt={`Image ${index + 1}`}
        width={500}
        height={500}
        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
      />
    </motion.div>
  )
}
